import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { getLastUrlParam } from '@utils/url';
import { getQueryParams } from '@utils/domRouter';
import canInitNewCart from '@utils/cart/canInitNewCart';
import { QUERY_PARAM } from '@commons/constants';
import { STATUS_CODE, URI_PARAM } from '@shared/constants';
import { SHOP_TAB_INDEX } from '@shopDetails/constants';
import ClientComponents from '@components/ClientComponents';
import { getShopDetailsRoute } from '@services/routes.service';
import MobileOnlyComponent from '@shared/components/MobileOnlyComponent';

// pages
import SectionDetails from '@shopDetails/pages/SectionDetails/Loadable';
import SectionDetailsFromSection from '@shopDetails/pages/SectionDetailsFromSection/Loadable';
import ShopInfo from '@shopDetails/pages/ShopInfo/Loadable';
import Reviews from '@shopDetails/pages/Reviews/Loadable';
import MenuSearch from '@shopDetails/pages/MenuSearch/Loadable';
import SearchSuggestions from '@shopDetails/pages/SearchSuggestions/Loadable';
import ShopDetailsExtended from '@shopDetails/components/ShopDetailsExtended/Loadable';
import ShopDetailsShelves from '@shopDetails/components/ShopDetailsShelves/Loadable';
import DesktopShopDetailsExtended from '@shopDetails/components/DesktopShopDetailsExtended/Loadable';
import DesktopShopDetailsShelves from '@shopDetails/components/DesktopShopDetailsShelves/Loadable';
import ProductConfiguration from '@productConfig/Loadable';
import Error from '@shared/containers/Error/Loadable';

import ModalManager, {
  ModalManagerPage,
} from '@shared/components/ModalManager';
import { USER_APPLICATION } from '@utils/constants';

import connect from './connect';
import preload from './preload';
import { MODAL_ID } from './constants';
import {
  isShopInfoRoute,
  isShopDetailsReviewRoute,
  isShopDetailsSearchRoute,
  isShopDetailsSectionRoute,
  modalActive,
} from './utils';

const { SHOP, CITY } = URI_PARAM;
const { FEATURED_PRODUCT_CONFIG, PRODUCT_CONFIG } = QUERY_PARAM;

class ShopDetailsRouter extends PureComponent {
  constructor(props) {
    super(props);
    const queryParams = new URLSearchParams(window.location.search);
    const paramsToRemove = ['source', 'gtin', 'productName'];
    paramsToRemove.forEach(param => queryParams.delete(param));
    const newQueryString = queryParams.toString();
    const newUrl =
      window.location.pathname + (newQueryString ? `?${newQueryString}` : '');
    window.history.replaceState({}, document.title, newUrl);
  }

  state = {
    productId: null,
  };

  hasTrackedShopDetailsLoaded = false;

  async componentDidMount() {
    const {
      shop,
      menu,
      cart,
      initializeCart,
      searchedLocation,
      fetchMenu,
      fetchShopByLink,
      hasFetchedMenu,
      hasFetchedShop,
      match: {
        params: { [SHOP]: shopLink },
      },
      isShelvesMenuLayout,
      isDesktopLayout,
      businessTypeFromQuery,
      isPickupFilter,
      fetchUserTotalOrders,
    } = this.props;

    let fetchedShop = { ...shop, menuId: shop?.menu?.id };
    const businessType = businessTypeFromQuery || fetchedShop?.businessType;

    if (!hasFetchedShop) {
      const fetchedShopByLink = await fetchShopByLink({ shopLink });
      fetchedShop = { ...fetchedShop, ...fetchedShopByLink };
    }

    const forceFetchMenu =
      fetchedShop?.menuId &&
      (!hasFetchedMenu || !menu || fetchedShop.menuId !== menu.id);
    fetchMenu({
      shop: fetchedShop,
      forceFetch: forceFetchMenu,
      isPickup: isPickupFilter,
    });
    if (canInitNewCart({ cart, shop: fetchedShop })) {
      initializeCart({
        restaurantId: fetchedShop.id,
        location: searchedLocation,
        joker: null,
        userApplication: isDesktopLayout
          ? USER_APPLICATION.WEB_DESKTOP
          : USER_APPLICATION.WEB_MOBILE,
        isPickup: isPickupFilter,
        businessType,
      });
    }
    preload({ isShelvesMenuLayout });
    fetchUserTotalOrders();
  }

  componentDidUpdate(prevProps) {
    const {
      hasFetchedMenu,
      hasFetchedShop,
      trackShopDetailsLoaded,
      isShelvesMenuLayout,
      isInitializingCart,
    } = this.props;

    if (
      (prevProps.hasFetchedMenu !== hasFetchedMenu ||
        prevProps.hasFetchedShop !== hasFetchedShop ||
        prevProps.isInitializingCart !== isInitializingCart) &&
      hasFetchedMenu &&
      hasFetchedShop &&
      !isInitializingCart &&
      !this.hasTrackedShopDetailsLoaded
    ) {
      this.hasTrackedShopDetailsLoaded = true;
      trackShopDetailsLoaded();
    }

    if (isShelvesMenuLayout && !prevProps.isShelvesMenuLayout) {
      preload({ isShelvesMenuLayout });
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { hasFetchedShop, hasFetchedMenu, location } = nextProps;
    let { productId } = prevState;

    if (hasFetchedShop && hasFetchedMenu && location) {
      const featuredProductId = getQueryParams(
        location,
        FEATURED_PRODUCT_CONFIG,
      );
      const simpleProductId = getQueryParams(location, PRODUCT_CONFIG);

      productId = featuredProductId || simpleProductId || null;
    }

    return {
      productId,
    };
  }

  getSectionId = () => {
    const { location } = this.props;
    return getLastUrlParam(location.pathname);
  };

  defaultComponent = () => {
    const { isShelvesMenuLayout, isDesktopLayout } = this.props;
    return isDesktopLayout ? (
      <ModalManagerPage
        default
        id={MODAL_ID.DEFAULT}
        render={() =>
          isShelvesMenuLayout ? (
            <DesktopShopDetailsShelves {...this.props} />
          ) : (
            <DesktopShopDetailsExtended {...this.props} />
          )
        }
      />
    ) : (
      <ModalManagerPage
        default
        id={MODAL_ID.DEFAULT}
        render={() =>
          isShelvesMenuLayout ? (
            <ShopDetailsShelves {...this.props} />
          ) : (
            <ShopDetailsExtended {...this.props} />
          )
        }
      />
    );
  };

  render() {
    const {
      groceriesTabSelected,
      shopId,
      userId,
      isShelvesMenuLayout,
      location: { pathname },
      match,
    } = this.props;
    const { productId } = this.state;

    const defaultRoute = getShopDetailsRoute({
      cityName: match.params[CITY],
      link: match.params[SHOP],
    });

    const shouldReturnNotFound =
      defaultRoute !== encodeURI(pathname) && !modalActive(pathname);
    if (shouldReturnNotFound)
      return <Error errorCode={STATUS_CODE.NOT_FOUND} />;

    return (
      <ModalManager>
        {this.defaultComponent()}
        <ModalManagerPage
          isActive={!!productId}
          id={MODAL_ID.PRODUCT_CONFIGURATION}
          render={() => (
            <ProductConfiguration {...this.props} productId={productId} />
          )}
        />
        <ModalManagerPage
          isActive={isShopInfoRoute(pathname)}
          id={MODAL_ID.SHOP_INFO}
          render={() => <ShopInfo {...this.props} />}
        />
        <ModalManagerPage
          isActive={isShopDetailsReviewRoute(pathname)}
          id={MODAL_ID.SHOP_REVIEW}
          render={() => <Reviews {...this.props} />}
        />
        <ModalManagerPage
          isActive={isShopDetailsSearchRoute(pathname)}
          id={MODAL_ID.SHOP_SEARCH}
          render={() => (
            <MobileOnlyComponent>
              {isShelvesMenuLayout ? (
                <ClientComponents>
                  <SearchSuggestions
                    shopId={shopId}
                    userId={userId}
                    {...this.props}
                  />
                </ClientComponents>
              ) : (
                <MenuSearch {...this.props} />
              )}
            </MobileOnlyComponent>
          )}
        />

        <ModalManagerPage
          isActive={isShopDetailsSectionRoute(pathname)}
          id={MODAL_ID.SHOP_SECTION}
          render={() => (
            <MobileOnlyComponent>
              {groceriesTabSelected === SHOP_TAB_INDEX ? (
                <SectionDetails
                  sectionId={this.getSectionId()}
                  {...this.props}
                />
              ) : (
                <SectionDetailsFromSection
                  sectionId={this.getSectionId()}
                  {...this.props}
                />
              )}
            </MobileOnlyComponent>
          )}
        />
      </ModalManager>
    );
  }
}

ShopDetailsRouter.propTypes = {
  // data
  shopId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.number,
  menu: PropTypes.object,
  shop: PropTypes.object,
  cart: PropTypes.object,
  match: PropTypes.shape({
    params: PropTypes.shape({
      city: PropTypes.string,
      restaurant: PropTypes.string,
    }),
  }),
  hasFetchedMenu: PropTypes.bool.isRequired,
  hasFetchedShop: PropTypes.bool.isRequired,
  isShelvesMenuLayout: PropTypes.bool.isRequired,
  location: PropTypes.object,
  history: PropTypes.object,
  searchedLocation: PropTypes.object,
  groceriesTabSelected: PropTypes.number,
  isDesktopLayout: PropTypes.bool,
  businessTypeFromQuery: PropTypes.string,
  isInitializingCart: PropTypes.bool,
  isPickupFilter: PropTypes.bool,

  // callbacks
  initializeCart: PropTypes.func.isRequired,
  fetchMenu: PropTypes.func.isRequired,
  fetchShopByLink: PropTypes.func.isRequired,
  trackShopDetailsLoaded: PropTypes.func.isRequired,

  // router actions
  pushPage: PropTypes.func.isRequired,

  fetchUserTotalOrders: PropTypes.func.isRequired,
};

export { ShopDetailsRouter as RouterTests };

export default connect(ShopDetailsRouter);
